// SamplingEventForm.js
// This is the main form for user to save, update, and complete various tasks for the sampling event.
// The form will contain tabs for different sections of the sampling event, allow for the user who created the event to see all actions and updates to the event in near real time.
// The form will have a view option for users to see wells outside of their assigned group wells.

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SamplingEventForm.module.css";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../store/appStore";
import { useUserState } from "../../../context/user";
import { useSamplingEventAutosave } from "../../../hooks/useSamplingEventAutosave";
// Sampling event components
import { getSamplingEquipmentMeters } from "../../../api/SamplingEventsApi";
import { BackToTableButton } from "../../landingPage/components/buttons/DelActions";
import { DailySummaryTab } from "./daily_summary/DailySummaryTab";
import { InspectionsTab } from "./inspections/InspectionsTab";
import { WellsTab } from "./wells/WellsTab";
import { CalibrationLogTab } from "./calibration_log/CalibrationLogTab";
import { FieldNotesTab } from "./field_notes/FieldNotesTab";
// Importing the JSA Tab
import { SamplingEventJsaTab } from "./jsa/JsaTab";

// Setting up the Sampling Event Form
export const SamplingEventForm = ({
  tableName,
  setShowSamplingEventForm,
  samplingEventMode,
  refreshListFunction = null,
}) => {
  // User
  const user = useUserState().currentUser;
  // Projects
  const allProjects = useAppStore((s) => s.projects.list, shallow);
  // Employees
  const allEmployees = useAppStore((s) => s.employees.list, shallow);
  // Active Sampling Event
  const activeSamplingEvent = useAppStore(
    (s) => s.activeSamplingEvent,
    shallow
  );
  const samplingEventData = activeSamplingEvent?.currentEvent;

  // Memoize the eventId to prevent unnecessary re-renders
  const eventId = useMemo(() => {
    return samplingEventData?.id || null;
  }, [samplingEventData?.id]);

  // Socket/autosave status (single source of truth)
  const { isConnected, roomStatus, emitPatch } =
    useSamplingEventAutosave(eventId);
  const isRoomJoined = roomStatus === "joined";
  const [samplingEquipmentMeters, setSamplingEquipmentMeters] = useState([]);

  // Assemble Sampling Event Form props/actions (mirroring DelForm pattern)
  const seFormUI = useAppStore(
    (s) => s.activeSamplingEvent?.samplingEventForm,
    shallow
  );
  const seFormActions = useAppStore(
    (s) => s.activeSamplingEventActions?.samplingEventFormActions,
    shallow
  );

  // console.log("seFormActions", seFormActions);
  // console.log("seFormUI", seFormUI);
  // console.log("samplingEventData", samplingEventData);
  // console.log("activeSamplingEvent", activeSamplingEvent);

  // Fetch sampling equipment meters once for this form
  useEffect(() => {
    let didCancel = false;
    (async () => {
      try {
        const res = await getSamplingEquipmentMeters();
        const meters = res?.data?.sampling_equipment_meters || [];
        if (!didCancel) setSamplingEquipmentMeters(meters);
      } catch (e) {
        if (!didCancel) setSamplingEquipmentMeters([]);
      }
    })();
    return () => {
      didCancel = true;
    };
  }, []);

  const effectiveMode = useMemo(() => {
    if (samplingEventData?.completed) return "view";
    return samplingEventMode;
  }, [samplingEventData?.completed, samplingEventMode]);

  const samplingEventTabProps = {
    samplingEventData,
    samplingEventMode: effectiveMode,
    allEmployees,
    allProjects,
    user,
    samplingEquipmentMeters,
    // Socket emitPatch for actions to use
    emitPatch,
    // UI buckets
    dailySummaryUI: seFormUI?.dailySummaryUI,
    calibrationLogUI: seFormUI?.calibrationLogUI,
    inspectionsUI: seFormUI?.inspectionsUI,
    wellsUI: seFormUI?.wellsUI,
    fieldNotesUI: seFormUI?.fieldNotesUI,
    // JSA UI
    activeJsaId: seFormUI?.jsaUI?.activeJsaId,
    jsaUICommon: seFormUI?.jsaUI?.jsaUICommon,
    jsaUIById: seFormUI?.jsaUI?.jsaUIById,
  };

  const samplingEventFormActionFunctions = {
    samplingEventFormActions: seFormActions,
    activeSamplingEventActions: useAppStore(
      (s) => s.activeSamplingEventActions,
      shallow
    ),
  };

  // Navigation back to table (replace legacy samplingEventLandingPageUtils)
  const navigate = useNavigate();
  const navigateToTab = useAppStore((s) => s.navigationActions.navigateToTab);
  const backToTableFunction = () => {
    navigateToTab(navigate, tableName);
    setShowSamplingEventForm(false);
    refreshListFunction?.();
  };

  // Effect to set the initial tab
  useEffect(() => {
    // Only set the initial tab if it hasn't been set yet
    // and the necessary data is available.
    if (!activeSamplingEvent.currentTab && user && samplingEventData) {
      let initialTab = "calibrationLogTab"; // Default for restricted users
      if (
        user.employee_id === samplingEventData.teamLead ||
        user.employee_id === samplingEventData.createdBy ||
        user.access_level === 2
      ) {
        initialTab = "dailySummaryTab";
      }
      try {
        useAppStore
          .getState()
          .activeSamplingEventActions.setCurrentTab(initialTab);
      } catch {}
    }
  }, [activeSamplingEvent.currentTab, user, samplingEventData]);

  if (!samplingEventData) {
    return <div>Loading Sampling Event...</div>;
  }

  return (
    <div className={styles.samplingEventFormContainer}>
      <>
        {/* Sampling Event Tabs Container */}
        {/* Setting some permission levels, if a user is not user_role 2, the team_lead, or the sampling event creator, they will not be able to see the daily summary tab */}
        <div className={styles.samplingEventTabMenuBar}>
          {(user?.employee_id === samplingEventData?.teamLead ||
            user?.employee_id === samplingEventData?.createdBy ||
            user?.access_level === 2) && (
            <button
              className={`${styles.samplingEventTabButton} ${
                activeSamplingEvent.currentTab === "dailySummaryTab"
                  ? styles.active
                  : ""
              }`}
              onClick={() =>
                useAppStore
                  .getState()
                  .activeSamplingEventActions.setCurrentTab("dailySummaryTab")
              }
            >
              Daily Summary
            </button>
          )}
          <button
            className={`${styles.samplingEventTabButton} ${
              activeSamplingEvent.currentTab === "calibrationLogTab"
                ? styles.active
                : ""
            }`}
            onClick={() =>
              useAppStore
                .getState()
                .activeSamplingEventActions.setCurrentTab("calibrationLogTab")
            }
          >
            Calibration Log
          </button>
          <button
            className={`${styles.samplingEventTabButton} ${
              activeSamplingEvent.currentTab === "inspectionsTab"
                ? styles.active
                : ""
            }`}
            onClick={() =>
              useAppStore
                .getState()
                .activeSamplingEventActions.setCurrentTab("inspectionsTab")
            }
          >
            Inspections
          </button>
          <button
            className={`${styles.samplingEventTabButton} ${
              activeSamplingEvent.currentTab === "wellsTab" ? styles.active : ""
            }`}
            onClick={() =>
              useAppStore
                .getState()
                .activeSamplingEventActions.setCurrentTab("wellsTab")
            }
          >
            Wells
          </button>
          <button
            className={`${styles.samplingEventTabButton} ${
              activeSamplingEvent.currentTab === "fieldNotesTab"
                ? styles.active
                : ""
            }`}
            onClick={() =>
              useAppStore
                .getState()
                .activeSamplingEventActions.setCurrentTab("fieldNotesTab")
            }
          >
            Field Notes
          </button>
          <button
            className={`${styles.samplingEventTabButton} ${
              activeSamplingEvent.currentTab === "jsaTab" ? styles.active : ""
            }`}
            onClick={() =>
              useAppStore
                .getState()
                .activeSamplingEventActions.setCurrentTab("jsaTab")
            }
          >
            JSA
          </button>
        </div>

        {/* Sampling Event Tab Content */}
        <div className={styles.samplingEventTabContent}>
          {(() => {
            // If currentTab is not yet set (e.g., initial render before useEffect runs),
            // render null or a loading indicator.
            if (!activeSamplingEvent.currentTab) {
              return null; // Or a loading spinner component
            }
            switch (activeSamplingEvent.currentTab) {
              case "dailySummaryTab":
                return (
                  <DailySummaryTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
              case "calibrationLogTab":
                return (
                  <CalibrationLogTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
              case "inspectionsTab":
                return (
                  <InspectionsTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
              case "wellsTab":
                return (
                  <WellsTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
              case "fieldNotesTab":
                return (
                  <FieldNotesTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
              case "jsaTab":
                return (
                  <SamplingEventJsaTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
              default:
                // Fallback if currentTab is an unexpected value.
                // Render a sensible default tab based on permissions.
                if (
                  user?.employee_id === samplingEventData?.teamLead ||
                  user?.employee_id === samplingEventData?.createdBy ||
                  user?.access_level === 2
                ) {
                  return (
                    <DailySummaryTab
                      samplingEventData={samplingEventData}
                      tabProps={samplingEventTabProps}
                      actions={samplingEventFormActionFunctions}
                    />
                  );
                }
                // Fallback to a universally accessible tab like CalibrationLogTab or InspectionsTab
                return (
                  <CalibrationLogTab
                    samplingEventData={samplingEventData}
                    tabProps={samplingEventTabProps}
                    actions={samplingEventFormActionFunctions}
                  />
                );
            }
          })()}
        </div>
      </>
      {/* Action Buttons Container */}
      <div className={styles.samplingEventActionButtonsContainer}>
        <div className={styles.rightSideTableActions}>
          <BackToTableButton
            backToTableFunction={backToTableFunction}
            tableName={tableName}
            backToText={false}
          />
        </div>
        <div className={styles.leftSideTableActions}>
          {/* New Connection Status Indicator */}
          <div className={styles.connectionStatusIndicator}>
            <div
              className={`${styles.connectionStatusCircle} ${
                isConnected ? styles.connectedCircle : styles.disconnectedCircle
              }`}
              title={
                isConnected
                  ? isRoomJoined
                    ? "Connected & Synced"
                    : "Connecting to event..."
                  : "Disconnected. Attempting to reconnect..."
              }
            ></div>
            {!isRoomJoined && (
              <span className={styles.connectionStatusText}>
                {isConnected ? "Joining Event..." : "Reconnecting..."}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
